import React from 'react';
import { Box, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { Functions as FnIcon } from '@mui/icons-material';

const PLACEHOLDER = [
  '1. Start with the [table / dataset] — all rows',
  '2. Filter: keep only rows where [condition]',
  '3. Group the remaining rows by [field]',
  '4. For each group, calculate [SUM / COUNT / AVG of field]',
  '5. Remove groups where [aggregate condition — e.g. total < 5000]',
  '6. Sort the result by [field] [ASC / DESC]',
  '7. Return the top [N] rows',
].join('\n');

const LogicFormStep = ({ config, response = {}, onChange, disabled }) => {
  const theme = useTheme();

  // Store as { text: '...' } — compatible with isStepAnswered
  const value = typeof response === 'string' ? response : (response.text || '');

  const handleChange = (e) => {
    const next = typeof response === 'string' ? e.target.value : { ...response, text: e.target.value };
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      {/* Banner */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        px: 1.5, py: 1.25,
        bgcolor: alpha(theme.palette.info.main, 0.07),
        borderLeft: `3px solid ${theme.palette.info.main}`,
        borderRadius: '0 6px 6px 0',
      }}>
        <FnIcon sx={{ fontSize: 18, color: 'info.main', mt: 0.15, flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" fontWeight={700} color="info.main">
            Write it as numbered steps — no code syntax needed
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
            Think in plain English: "1. Start with the orders table. 2. Keep only rows where order_value &gt; 100. 3. Group by country…"
            This is pseudocode — human-readable logic that a non-programmer could follow.
          </Typography>
        </Box>
      </Box>

      {/* Pseudocode editor */}
      <TextField
        value={value}
        onChange={handleChange}
        placeholder={PLACEHOLDER}
        multiline
        minRows={8}
        fullWidth
        disabled={disabled}
        variant="outlined"
        size="small"
        inputProps={{
          style: {
            fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
            fontSize: '0.84rem',
            lineHeight: 1.85,
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: alpha(theme.palette.info.main, 0.02),
          },
        }}
      />

      <Typography variant="caption" color="text.secondary">
        Tip: each numbered step should map to one clause in your SQL or one line of code. If you can write the steps, you understand the solution.
      </Typography>
    </Stack>
  );
};

export default LogicFormStep;
